import { Field, useField, useForm } from 'react-final-form';
import styles from './TableUpload.module.scss';
import { useCallback, useContext, useRef, useState } from 'react';
import { pdfProtectionStates, UnderwritingFormStatus, UnderwritingSteps, uploadStates } from '../../../../interfaces/interfaces';
import API from '../../../../rest/api';
import { getIn } from 'final-form';
import { idProofInitialState, UnderwritingFormContext } from '../../context/UnderwritingFormContext';
import MeritoButton from '../../../../components/common/MeritoButtons/MeritoButtons';
import { MdOutlineFileUpload as FileUploadIcon } from 'react-icons/md';
import { RiCheckboxCircleFill as SuccessIcon, RiDeleteBin6Line as BinIcon } from 'react-icons/ri';
import CircularLoader from '../../../../components/common/CircularLoader/CircularLoader';
import { useSpring, animated } from 'react-spring';
import { easings } from '@react-spring/web';
import { v1 as uuidv1 } from 'uuid';

import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "../../CompanyDetails/pdfWorker" // Adjust the path as necessary
import { checkPDFProtection } from '../utils/functions';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

// Renamed from Props to TableUploadProps to avoid conflict
type TableUploadProps = {
  fieldName: string;
  showUploadIcon: boolean;
  formCompleteStatus: { [x: string]: UnderwritingFormStatus };
  stepName: UnderwritingSteps;
  documentType: string;
  fileSizeLimit?: number;
  isMultiFileUpload?: boolean;
  newUploadedDocIds: number[];
  setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  deleteDocIds: number[],
  setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  setPasswordSuccess?: (value: boolean) => void;
  formContext?: any
};

export const validateFile = (file: File | undefined) => {
  const fileSizeLimit = 30;
  const allowedFormats = ['image/jpeg', 'image/png', 'application/pdf'];
  const maxFileSize = fileSizeLimit * 1024 * 1024; // fileSizeLimit in MB

  if (!file) {
    return "Upload document";
  }

  if (!allowedFormats.includes(file.type)) {
    return "Only JPG, PNG and PDF files are accepted";
  }
  if (file.size > maxFileSize) {
    return `File size exceeds ${fileSizeLimit}MB`;
  }
  return undefined;
};

const TableUpload = ({
  fieldName,
  showUploadIcon,
  formCompleteStatus,
  documentType,
  stepName,
  fileSizeLimit = 30,
  isMultiFileUpload = false,
  deleteDocIds,
  setDeleteDocIds,
  newUploadedDocIds,
  setNewUploadedDocIds,
  setPasswordSuccess = () => { },
  formContext = null
}: TableUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getState, mutators: { setFieldTouched }, change, batch } = useForm();
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [showUploadFileState, setShowUploadFileState] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [initialValidationError, setInitialValidationError] = useState(null);


  const { handleUnderwritingFileJobs, underwritingFileJobs, sendFileJobToApi } = useContext(formContext ? formContext : UnderwritingFormContext) as any;

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };





  const uploadFileToS3 = async (fieldName: string, fileData: File, fileJob: any, fileFormJob: any,) => {
    try {
      // await addDelay(5000);
      const formData = new FormData();
      formData.append('documentType', documentType);
      formData.append('file', fileData);
      const data = await API.post('/app/uploadDocs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then((res) => res.data);
      let jobDetails = null;
      console.log('fileData.type', fileData.type);
      if (fileData.type == 'application/pdf') {
        const isPdfProtected = await checkPDFProtection(fileData);
        // console.log('isPdfProtected', isPdfProtected);
        if (isPdfProtected == pdfProtectionStates.passwordNotRequired) {
          // password not required
          jobDetails = await sendFileJobToApi(stepName, fieldName, {
            s3FileKey: data?.Key,
            fileMetaData: {
              fileName: fileData?.name,
              fileType: fileData?.type,
              requiredPassword: false,
              password: null,
            },
            status: uploadStates.doneUploading,
          })
        } else if (isPdfProtected == pdfProtectionStates.passwordRequired) {
          // password required
          // console.log('password required');
          jobDetails = await sendFileJobToApi(stepName, fieldName, {
            s3FileKey: data?.Key,
            fileMetaData: {
              fileName: fileData?.name,
              fileType: fileData?.type,
              requiredPassword: true,
              password: null,
            },
            status: uploadStates.requiredPassword,
          })
        }
      } else {
        jobDetails = await sendFileJobToApi(stepName, fieldName, {
          s3FileKey: data?.Key,
          fileMetaData: {
            fileName: fileData?.name,
            fileType: fileData?.type,
            requiredPassword: false,
            password: null,
          },
          status: uploadStates.doneUploading,
        })
      }
      // console.log('coming here')
      setNewUploadedDocIds(prev => [...prev, jobDetails?.docId]);
      change(`${fieldName}`, {
        ...fileFormJob,
        uploadedFile: {
          fileKey: data?.Key,
          fileName: fileData?.name,
          fileType: fileData?.type,
          docId: jobDetails?.id,
        }
      });
    } catch (error) {
      console.log('error', error);
    }
  }



  const uploadFile = async (fileData: File) => {
    const formData = new FormData();
    formData.append('documentType', documentType);
    formData.append('file', fileData);
    const fileFormObj = {
      selectedFile: fileData,
      uploadedFile: null,
    }
    const fileJob = {
      jobId: uuidv1(),
      step: stepName,
      section: documentType,
      formFieldName: fieldName,
      s3FileKey: null,
      fileMetaData: {
        fileName: fileData.name,
        fileType: fileData.type,
        requiredPassword: false,
        password: null,
      },
      status: uploadStates.uploading,
    }
    change(fieldName, fileFormObj)
    handleUnderwritingFileJobs(stepName, fieldName, fileJob);
    uploadFileToS3(fieldName, fileData, fileJob, fileFormObj);
  };


  const deleteDoc = async (docId: number) => {
    try {
      console.log('docId', docId)
      setDeleteDocIds(prev => [...prev, docId]);
      change(`${fieldName}`, null);
      setShowUploadFileState(false);
    } catch (error) {
      console.log('error', error);
    } finally {
      setFileUploadLoading(false);
    }
  };

  const { input: { value: fileUploadedValue } } = useField(`${fieldName}`, { subscription: { value: true } });
  const { meta: idProofFileMeta, input: { value: fileSelectedValue } } = useField(`${fieldName}.selectedFile`, { subscription: { touched: true, error: true, value: true } });

  const expandStyles = useSpring(
    isHovered
      ? {
        from: { width: 50 },
        to: { width: 155 },
        config: { duration: 100 }
      }
      : {
        from: { width: 155 },
        to: { width: 50 },
        config: { duration: 75 }
      }
  );

  const fadeStyles = useSpring({
    opacity: isHovered ? 1 : 0
  });

  const getJobDetails = useCallback((fieldName) => {
    const jobDetails = underwritingFileJobs?.[stepName]?.[fieldName] ?? null;
    // console.log('jobDetails', jobDetails);
    return jobDetails;
  }, [underwritingFileJobs]);

  // console.log('initialValidationError', initialValidationError);
  // const required = value => (value ? undefined : 'Required')

  return (
    <div id={`${fieldName}`} className={styles.fileInputContainer}>
      <>
        {(fileUploadedValue?.selectedFile instanceof File || fileUploadedValue?.uploadedFile) ? (
          <animated.div
            className={styles.iconRootContainer}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={expandStyles}
          >
            {getJobDetails(fieldName)?.status == uploadStates.uploading ? (
              <CircularLoader />
            ) : (
              <div className={styles.iconContainer}>
                {!isHovered && <SuccessIcon className={styles.successIcon} />}
                {isHovered && (
                  <animated.div style={fadeStyles} className={styles.fileInfo}>
                    <div className={styles.fileName}>{fileUploadedValue?.uploadedFile.fileName || fileSelectedValue?.name}</div>
                    <div className={styles.actionContainer} onClick={() => {
                      console.log('fileUploadedValue?.uploadedFile.docId', fileUploadedValue)
                      deleteDoc(fileUploadedValue?.uploadedFile.docId);
                      setPasswordSuccess(false);
                    }}>
                      <BinIcon />
                    </div>
                  </animated.div>
                )}
              </div>
            )}
          </animated.div>
        ) : !showUploadIcon ? (
          <div>-</div>
        ) : (
          <Field
            name={`${fieldName}.selectedFile`}
            validate={(values) => validateFile(values)}
          >
            {(props) => {
              return (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      e.preventDefault();

                      // Call the onFileChange prop here
                      // console.log('file', e.target.files);
                      const file = e.target.files?.[0];
                      const error = validateFile(file);
                      console.log('fieldName', fieldName);


                      if (error) {
                        console.log('error', error);
                        // setInitialValidationError(error ? error?.toString() : "");
                        setInitialValidationError(error)
                        // props.input.onChange({
                        //   selectedFile: file,
                        //   uploadedFile: null,
                        // }); // Optionally handle error state here
                      } else {
                        setInitialValidationError("")
                        // props.input.onChange(file);
                        // if (isHovered) {
                        //   setIsHovered(false);
                        // }
                        uploadFile(file);
                        setShowUploadFileState(true);
                        setFieldTouched(fieldName);
                        // }
                      }
                    }}
                    accept=".jpg,.jpeg,.png,.pdf"
                  />
                  <MeritoButton
                    type="tertiary"
                    size="md"
                    onlyIcon={true}
                    icon={<FileUploadIcon />}
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = ""; // 🔥 Reset the input field
                      }
                      handleButtonClick();
                      setInitialValidationError(null);
                    }}
                  />
                  {initialValidationError && !(props.meta.error && props.meta.touched) && <div className={styles.errorText}>{initialValidationError}</div>}
                  {(props.meta.error && props.meta.touched) ? (
                    <div className={styles.errorText}>{props.meta?.error}</div>
                  ) : null}
                </>
              )
            }}
          </Field>
        )}
      </>
    </div >
  );
};

export default TableUpload;
